import { useEffect, useMemo, useState } from "react";
import Button from "../../../components/ui/Button/Button";
import { teenExperienceReportApi } from "../../../lib/teenExperienceReport";
import type { TeenExperienceReportResponse } from "../../../types/teenExperienceReportTypes";
import { apiErrorToMessage } from "../../../utils/ApiUtils";
import { formatEstimatedDuration, formatDateTime } from "../../../utils/DateUtils";
import { formatTeenGigCompensation } from "../../../utils/GigCompensationUtils";
import { formatCityStateZip } from "../../../utils/LocationUtils";

const formatHours = (minutes: number) => {
  if (!minutes) {
    return "0 hrs";
  }
  const hours = minutes / 60;
  return `${Number.isInteger(hours) ? hours.toFixed(0) : hours.toFixed(1)} hrs`;
};

const TeenExperienceReportPage = () => {
  const [report, setReport] = useState<TeenExperienceReportResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const response = await teenExperienceReportApi.get();
        if (!cancelled) {
          setReport(response);
        }
      } catch (requestError) {
        if (!cancelled) {
          setError(apiErrorToMessage(requestError, "Failed to load the experience report."));
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, []);

  const generatedLabel = useMemo(() => formatDateTime(report?.generatedAt ?? null), [report?.generatedAt]);

  return (
    <main className="mx-auto max-w-6xl px-6 py-12">
      <section className="overflow-hidden rounded-[2rem] border border-myteengig-border bg-[radial-gradient(circle_at_top_left,_rgba(110,180,96,0.22),_transparent_28%),linear-gradient(135deg,_rgba(255,255,255,1),_rgba(244,247,240,1))] shadow-sm">
        <div className="flex flex-col items-center gap-6 px-6 py-8 text-center lg:px-8">
          <div className="max-w-3xl">
            <p className="text-sm font-semibold uppercase tracking-[0.22em] text-myteengig-primary">Teen Workspace</p>
            <h1 className="mt-3 text-3xl font-bold tracking-tight text-myteengig-text-primary sm:text-4xl">Experience Report</h1>
            <p className="mt-4 text-base leading-7 text-myteengig-text-muted">
              Summarize completed gigs, reviews, and the kinds of work you have handled so far on MyTeenGig.
            </p>
            {report ? (
              <p className="mt-4 text-sm text-myteengig-text-muted">Generated {generatedLabel}</p>
            ) : null}
          </div>
          <div className="flex shrink-0 items-center gap-3">
            <a href={teenExperienceReportApi.pdfUrl()} target="_blank" rel="noreferrer">
              <Button disabled={!report || isLoading}>Download PDF</Button>
            </a>
          </div>
        </div>
      </section>

      {isLoading ? <p className="mt-6 text-myteengig-text-muted">Loading your experience report...</p> : null}
      {error ? <p className="mt-6 text-sm font-medium text-red-600">{error}</p> : null}

      {!isLoading && !error && report ? (
        <div className="mt-6 space-y-6">
          <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            {[
              ["Completed gigs", report.summary.totalCompletedGigs.toString()],
              ["Volunteer opportunities", report.summary.totalVolunteerGigs.toString()],
              ["Categories worked", report.summary.totalCategoriesWorked.toString()],
              ["Estimated hours", formatHours(report.summary.totalEstimatedMinutes)],
              ["Average rating", report.summary.averageRating !== null ? `${report.summary.averageRating.toFixed(1)} / 5` : "N/A"],
              ["Reviews received", report.summary.reviewCount.toString()],
              ["Repeat posters", report.summary.repeatPostersWorkedWith.toString()],
            ].map(([label, value]) => (
              <article key={label} className="rounded-[1.5rem] border border-myteengig-border bg-white p-5 shadow-sm">
                <p className="text-sm font-medium text-myteengig-text-muted">{label}</p>
                <p className="mt-2 text-2xl font-semibold text-myteengig-text-primary">{value}</p>
              </article>
            ))}
          </section>

          <section className="grid gap-6 xl:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)]">
            <article className="rounded-[1.75rem] border border-myteengig-border bg-white p-6 shadow-sm">
              <h2 className="text-xl font-semibold text-myteengig-text-primary">Category Breakdown</h2>
              <div className="mt-5 space-y-4">
                {report.categoryBreakdown.length === 0 ? (
                  <p className="text-sm text-myteengig-text-muted">No completed gigs yet.</p>
                ) : (
                  report.categoryBreakdown.map((item) => (
                    <div key={item.category} className="rounded-2xl border border-myteengig-border bg-myteengig-background px-4 py-4">
                      <div className="flex items-center justify-between gap-4">
                        <p className="font-semibold text-myteengig-text-primary">{item.category}</p>
                        <p className="text-sm text-myteengig-text-muted">{item.completedGigCount} gigs</p>
                      </div>
                      <div className="mt-2 flex flex-wrap gap-3 text-sm text-myteengig-text-muted">
                        <span>{formatHours(item.totalEstimatedMinutes)}</span>
                        <span>{item.averageRating !== null ? `${item.averageRating.toFixed(1)} / 5 avg` : "No ratings yet"}</span>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </article>

            <article className="rounded-[1.75rem] border border-myteengig-border bg-white p-6 shadow-sm">
              <h2 className="text-xl font-semibold text-myteengig-text-primary">Review Highlights</h2>
              <div className="mt-5 space-y-4">
                {report.highlightedReviews.length === 0 ? (
                  <p className="text-sm text-myteengig-text-muted">No written review highlights yet.</p>
                ) : (
                  report.highlightedReviews.map((review) => (
                    <div key={review.reviewId} className="rounded-2xl border border-myteengig-border bg-myteengig-background px-4 py-4">
                      <div className="flex flex-wrap items-center justify-between gap-3">
                        <p className="font-semibold text-myteengig-text-primary">{review.gigTitle}</p>
                        <p className="text-sm text-myteengig-text-muted">
                          {review.rating !== null ? `${review.rating.toFixed(1)} / 5` : "No rating"}
                        </p>
                      </div>
                      <p className="mt-1 text-xs uppercase tracking-wide text-myteengig-text-muted">{review.reviewerDisplayName}</p>
                      <p className="mt-3 text-sm leading-6 text-myteengig-text-primary">{review.message}</p>
                    </div>
                  ))
                )}
              </div>
            </article>
          </section>

          <section className="rounded-[1.75rem] border border-myteengig-border bg-white p-6 shadow-sm">
            <div className="flex items-center justify-between gap-4">
              <h2 className="text-xl font-semibold text-myteengig-text-primary">Completed Gig History</h2>
              <p className="text-sm text-myteengig-text-muted">{report.completedGigs.length} entries</p>
            </div>
            <div className="mt-5 space-y-4">
              {report.completedGigs.length === 0 ? (
                <p className="text-sm text-myteengig-text-muted">No completed gigs yet.</p>
              ) : (
                report.completedGigs.map((gig) => (
                  <article key={gig.gigRequestId} className="rounded-2xl border border-myteengig-border bg-myteengig-background px-4 py-4">
                    <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                      <div className="min-w-0">
                        <h3 className="text-lg font-semibold text-myteengig-text-primary">{gig.gigTitle}</h3>
                        <p className="mt-1 text-sm text-myteengig-text-muted">
                          {gig.category} • {gig.posterDisplayName} • {formatCityStateZip(gig)}
                        </p>
                        {gig.volunteer ? (
                          <p className="mt-2 inline-flex rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-emerald-800">
                            Volunteer
                          </p>
                        ) : null}
                      </div>
                      <div className="text-sm text-myteengig-text-muted">{formatDateTime(gig.completedAt)}</div>
                    </div>
                    <div className="mt-4 flex flex-wrap gap-4 text-sm text-myteengig-text-muted">
                      <span>{formatEstimatedDuration(gig.estimatedDurationMinutes)}</span>
                      <span>{formatTeenGigCompensation(gig.paymentAmountCents, gig.volunteer, "Pay not available")}</span>
                      <span>{gig.rating !== null ? `${gig.rating.toFixed(1)} / 5 rating` : "No rating yet"}</span>
                    </div>
                    {gig.reviewSnippet ? (
                      <p className="mt-3 text-sm leading-6 text-myteengig-text-primary">{gig.reviewSnippet}</p>
                    ) : null}
                  </article>
                ))
              )}
            </div>
          </section>
        </div>
      ) : null}
    </main>
  );
};

export default TeenExperienceReportPage;
